import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { getSidebarNavigation, APP_NAVIGATION } from "@/config/navigation";
import { fetchTenantFeatures } from "@/services/tenantApi";
import type {
  NavSectionConfig,
  TenantConfig,
  TenantFeatureKey,
} from "@/types/tenantFeatures";

type TenantFeaturesContextValue = {
  config: TenantConfig | null;
  loading: boolean;
  error: string | null;
  navigation: NavSectionConfig[];
  /** True when the API flag is on (e.g. features.market_reports). */
  isFeatureEnabled: (key: TenantFeatureKey) => boolean;
  /** True when Box Files tab and embed should show. */
  showBoxFiles: boolean;
  /** True when LLM Config nav and /api/prompts are allowed for this user. */
  showPromptAdmin: boolean;
};

const TenantFeaturesContext = createContext<
  TenantFeaturesContextValue | undefined
>(undefined);

export function TenantFeaturesProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadTenantConfig() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchTenantFeatures();

        if (response.success && response.data) {
          setConfig(response.data);
        } else {
          setConfig(null);
          if (typeof response.message === "string") {
            setError(response.message);
          } else {
            setError("Failed to load tenant configuration");
          }
        }
      } catch (loadError) {
        console.error("Failed to load tenant configuration:", loadError);
        setConfig(null);
        setError("Failed to load tenant configuration");
      } finally {
        setLoading(false);
      }
    }

    loadTenantConfig();
  }, []);

  const features = config?.features;

  const showPromptAdmin =
    config?.prompt_admin_enabled === true && config?.is_prompt_admin === true;

  let navigation: NavSectionConfig[] = [];
  if (features) {
    navigation = getSidebarNavigation(
      APP_NAVIGATION,
      features,
      showPromptAdmin,
    );
  }

  function isFeatureEnabled(key: TenantFeatureKey) {
    return features?.[key] === true;
  }

  const showBoxFiles =
    features?.box_integration === true && config?.box_provider !== "disabled";

  const value: TenantFeaturesContextValue = {
    config,
    loading,
    error,
    navigation,
    isFeatureEnabled,
    showBoxFiles,
    showPromptAdmin,
  };

  return (
    <TenantFeaturesContext.Provider value={value}>
      {children}
    </TenantFeaturesContext.Provider>
  );
}

export function useTenantFeatures() {
  const context = useContext(TenantFeaturesContext);
  if (!context) {
    throw new Error(
      "useTenantFeatures must be used inside TenantFeaturesProvider",
    );
  }
  return context;
}
